import { FastBookingSlots } from "@/types/booking";
import { format } from "date-fns";

/**
 * Checks if a specific date and time is available for fast booking
 * @param date The date to check
 * @param time The time to check (format: "HH:MM")
 * @param fastBookingSlots The fast booking slots configuration
 * @returns true if the slot is available for fast booking, false otherwise
 */
export const isFastBookingSlot = (
  date: Date,
  time: string,
  fastBookingSlots?: FastBookingSlots | null,
): boolean => {
  if (!fastBookingSlots) return false;

  // First check specific dates (these have priority)
  const dateString = format(date, "yyyy-MM-dd");
  if (fastBookingSlots.specific && fastBookingSlots.specific[dateString]) {
    return fastBookingSlots.specific[dateString].includes(time);
  }

  // Then check recurring slots
  if (fastBookingSlots.recurring) {
    // Day of week as number (0 is Sunday, 1 is Monday, etc.)
    let dayOfWeek = date.getDay();

    // Convert to ISO format where 1 = Monday, 7 = Sunday
    dayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

    const dayKey = dayOfWeek.toString();

    if (fastBookingSlots.recurring[dayKey]) {
      return fastBookingSlots.recurring[dayKey].includes(time);
    }
  }

  return false;
};

/**
 * Get all fast booking slots for a specific date
 * @param date The date to get slots for
 * @param fastBookingSlots The fast booking slots configuration
 * @returns Array of time slots available for fast booking
 */
export const getFastBookingSlots = (
  date: Date,
  fastBookingSlots?: FastBookingSlots | null,
): string[] => {
  if (!fastBookingSlots) return [];

  const slots: string[] = [];

  // First check specific dates
  const dateString = format(date, "yyyy-MM-dd");
  if (fastBookingSlots.specific && fastBookingSlots.specific[dateString]) {
    slots.push(...fastBookingSlots.specific[dateString]);
  }

  // Then check recurring slots
  if (fastBookingSlots.recurring) {
    // Day of week as number (0 is Sunday, 1 is Monday, etc.)
    let dayOfWeek = date.getDay();

    // Convert to ISO format where 1 = Monday, 7 = Sunday
    dayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

    const dayKey = dayOfWeek.toString();

    if (fastBookingSlots.recurring[dayKey]) {
      slots.push(...fastBookingSlots.recurring[dayKey]);
    }
  }

  // Filter duplicates and sort
  return [...new Set(slots)].sort();
};
